<template>
  <div class="space-y-8">
    <!-- User Details Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-gray-900">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>

            <h3 class="text-lg font-semibold text-gray-900">
              {{ $t('ProjectSettings.UserTab.headers.userDetails') }}
            </h3>
          </div>

          <button
            type="button"
            class="btn btn--primary-blue flex items-center space-x-2"
            data-test="save-head-contract"
            :disabled="updateMeAction.is(States.LOADING)"
            @click="updateMe"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

            <span>{{ $t('ProjectSettings.UserTab.button.saveDetails') }}</span>
          </button>
        </div>
      </div>

      <div class="p-6 w-1/4">
        <div class="flex flex-col">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('ProjectSettings.UserTab.label.language') }}
          </label>

          <AppLanguageSelector />
        </div>

        <form
          class="mt-6"
          method="post"
          @submit.prevent="updateMe"
        >
          <div class="space-y-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('ProjectSettings.UserTab.label.name') }}
              </label>

              <AppInput
                v-model="userForm.form.name"
                :error="userForm.getErrors('name')"
                class="form-input"
                type="text"
                name="name"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('ProjectSettings.UserTab.label.qualifications') }}
              </label>

              <AppInput
                v-model="userForm.form.qualifications"
                :error="userForm.getErrors('qualifications')"
                class="form-input"
                type="text"
                name="qualifications"
                placeholder="Enter your qualifications"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ $t('ProjectSettings.UserTab.label.position') }}
              </label>

              <AppInput
                v-model="userForm.form.position"
                :error="userForm.getErrors('position')"
                class="form-input"
                type="text"
                name="position"
                placeholder="Enter your position"
              />
            </div>
          </div>
        </form>
      </div>
    </div>

    <div
      v-if="!userStore.isAdmin"
      class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      <div class="card__header font-semibold">
        <div class="flex justify-between items-center">
          <p>
            {{ $t('ProjectSettings.UserTab.headers.notificationPreferences') }}
          </p>
        </div>
      </div>

      <div class="p-6">
        <form
          method="post"
          class="flex flex-col space-y-6"
          @submit.prevent="updateMe"
        >
          <div
            v-for="key in notificationSettingsKeys"
            :key="key"
            class="bg-gray-50 rounded-lg p-4 space-y-3"
          >
            <div class="flex items-center justify-between">
              <label
                :for="`notification-${key}-all`"
                class="text-sm font-semibold text-gray-900 cursor-pointer"
              >
                {{ notificationSettingsLabels[key] }}
              </label>

              <label
                :for="`notification-${key}-all`"
                class="flex items-center space-x-2 cursor-pointer"
              >
                <span class="text-xs text-gray-600">All Projects</span>

                <input
                  :id="`notification-${key}-all`"
                  type="checkbox"
                  class="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                  :checked="isAllProjects(key)"
                  :true-value="true"
                  :false-value="false"
                  @change="handleAllProjectsChange(key, $event)"
                >
              </label>
            </div>

            <div
              v-if="!isAllProjects(key)"
              class="mt-4 space-y-3"
            >
              <p class="text-xs text-gray-600">
                Select Individual Projects:
              </p>

              <div class="relative">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400">
  <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
</svg>

                <AppInput
                  v-model="projectSearch[key]"
                  type="text"
                  name="project-search"
                  class="form-input pl-10"
                  placeholder="Search projects..."
                />
              </div>

              <div class="max-h-48 overflow-y-auto border border-gray-200 rounded-md bg-white p-2 space-y-2">
                <label
                  v-for="project in filteredProjects(key)"
                  :key="project.id"
                  class="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <input
                    :id="`notification-${key}-project-${project.id}`"
                    type="checkbox"
                    class="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                    :checked="isProjectSelected(key, project.id)"
                    @change="handleProjectCheckboxChange(key, project.id, $event)"
                  >

                  <span class="text-sm text-gray-700">
                    {{ project.name }}
                  </span>
                </label>

                <p
                  v-if="filteredProjects(key).length === 0"
                  class="text-sm text-gray-500 text-center py-4"
                >
                  No projects found
                </p>
              </div>

              <div
                v-if="getSelectedProjectIds(key).length > 0"
                class="flex flex-wrap gap-2"
              >
                <span
                  v-for="projectId in getSelectedProjectIds(key)"
                  :key="projectId"
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {{ getProjectName(projectId) }}
                  <button
                    type="button"
                    class="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-blue-200"
                    @click="removeProject(key, projectId)"
                  >
                    <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-3 h-3">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18L18 6M6 6l12 12"
    />
</svg>
                  </button>
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div class="flex items-center space-x-3">
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

          <h3 class="text-lg font-semibold text-gray-900">
            Signature
          </h3>
        </div>
      </div>

      <div class="p-6">
        <div class="max-w-2xl mx-auto">
          <div class="text-center">
            <div class="mb-6">
              <FileUpload
                v-model="fileUploadForm"
                accept="image/jpeg,image/png,application/pdf"
                :uploading="storeSignatureAction.is(States.LOADING)"
                @change="fileUploaded"
              />
            </div>

            <div
              v-if="signatureMedia.mediaCollection.value.length > 0"
              class="mt-6"
            >
              <MediaDisplay
                :media="signatureMedia.mediaCollection.value[0]"
                class="max-w-md mx-auto"
                @delete="signatureMedia.removeMedia($event.uuid); userStore.userSignature = undefined;"
              />
            </div>

            <div
              v-else
              class="text-center py-8"
            >
              <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="mx-auto h-12 w-12 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

              <h3 class="mt-2 text-sm font-medium text-gray-900">
                No signature uploaded
              </h3>

              <p class="mt-1 text-sm text-gray-500">
                Upload an image or PDF to set as your digital signature.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AccessTokensSection />
  </div>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import MediaDisplay from '@/components/MediaDisplay.vue';
import AccessTokensSection from '@/components/ProjectSettings/UserTab/AccessTokensSection.vue';
import { useFormData } from '@/composables/useFormData';
import { useMediaCollection } from '@/composables/useMediaCollection';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import Project from '@/models/Project';
import { useI18nStore } from '@/store/i18n';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import {
  NOTIFICATION_SETTINGS_KEYS,
  NOTIFICATION_SETTINGS_LABELS,
  type NotificationSettings,
} from '@/types/notification-settings';
import Swal from 'sweetalert2';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const emits = defineEmits<{
  (e: 'dirty');
  (e: 'saved');
}>();

const userStore = useUserStore();
const projectsStore = useProjectsStore();

// Build default notification settings object with all keys initialized to false
const defaultNotificationSettings = NOTIFICATION_SETTINGS_KEYS.reduce((acc, key) => {
  acc[key] = false;

  return acc;
}, {} as NotificationSettings);

const userForm = useFormData({
  name: '',
  qualifications: '',
  position: '',
  notification_settings: defaultNotificationSettings,
});

watch(() => userStore.user, (newValue) => {
  if(newValue) {
    // Ensure form has all notification keys present for reactive updates
    userForm.setData({ notification_settings: defaultNotificationSettings });
    userForm.setData(newValue);

    nextTick(() => {
      userForm.resetDirtyStatus();
    });
  }
}, { immediate: true });

const updateMeAction = useStoreApiAction(userStore.updateMe);

const updateMe = () => {
  userForm.resetErrors();

  updateMeAction.request(userForm.form).then((data) => {
    Swal.fire({
      icon: 'success',
      text: t('ProjectSettings.UserTab.messages.detailsUpdated'),
    });

    userStore.user.position = data.position;
    userStore.user.name = data.name;
    userStore.user.notification_settings = data.notification_settings;
    userStore.user.qualifications = data.qualifications;
  }).catch((error) => {
    userForm.setErrors(error.data);
  });
};

watch(userForm.isDirty, (newValue) => {
  if(newValue) {
    emits('dirty');
  } else {
    emits('saved');
  }
});

const signatureMedia = useMediaCollection(userStore.showSignature);

watch(() => userStore.user.id, (newValue) => {
  signatureMedia.clear();

  signatureMedia.fetchMedia().then((media) => {
    userStore.userSignature = media[0];
  });
}, { immediate: true });

const storeSignatureAction = useStoreApiAction(userStore.storeSignature);
const fileUploadForm = ref();

const fileUploaded = (event: Event) => {
  const files = Array.from((event.target as HTMLInputElement).files);
  const selectedFile = files[0];

  if(selectedFile) {
    const fileFormData = new FormData();

    fileFormData.append('images[]', selectedFile);

    storeSignatureAction.request(fileFormData).then((medias) => {
      signatureMedia.clear();
      signatureMedia.addMedia(medias);
      fileUploadForm.value = [];
    }).catch((error) => {
      if(error.status === 422) {
        Swal.fire({
          icon: 'error',
          title: 'Upload Failed.',
          text: error.data.message,
        });
      }
    });
  }
};

// Make notification settings constants available in template
const notificationSettingsKeys = NOTIFICATION_SETTINGS_KEYS;
const notificationSettingsLabels = NOTIFICATION_SETTINGS_LABELS;

// Get available projects (non-archived)
const availableProjects = computed(() => {
  return projectsStore.models.whereNull('archived_at').get();
});

// Project search per notification key
const projectSearch = ref<Record<string, string>>({});

// Initialize search for each notification key
NOTIFICATION_SETTINGS_KEYS.forEach((key) => {
  projectSearch.value[key] = '';
});

// Filter projects based on search
const filteredProjects = (key: string) => {
  const searchTerm = (projectSearch.value[key] || '').toLowerCase();

  if(!searchTerm) {
    return availableProjects.value;
  }

  return availableProjects.value.filter((project) => project.name.toLowerCase().includes(searchTerm));
};

// Get project name by ID
const getProjectName = (projectId: number): string => {
  const project = availableProjects.value.find((p) => p.id === projectId);

  return project?.name || `Project ${projectId}`;
};

// Check if a notification setting is set to "all projects" (true)
const isAllProjects = (key: string): boolean => {
  const value = userForm.form.notification_settings[key];

  return value === true;
};

// Get selected project IDs for a notification setting
const getSelectedProjectIds = (key: string): number[] => {
  const value = userForm.form.notification_settings[key];

  if(Array.isArray(value)) {
    return value.map((id) => Number(id));
  }

  return [];
};

// Check if a project is selected for a notification setting
const isProjectSelected = (key: string, projectId: number): boolean => {
  const selectedIds = getSelectedProjectIds(key);

  return selectedIds.includes(projectId);
};

// Handle "All Projects" checkbox change
const handleAllProjectsChange = (key: string, event: Event) => {
  const target = event.target as HTMLInputElement;

  if(target.checked) {
    userForm.form.notification_settings[key] = true;
  } else {
    userForm.form.notification_settings[key] = false;
  }
};

// Handle project checkbox change
const handleProjectCheckboxChange = (key: string, projectId: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  const currentIds = getSelectedProjectIds(key);

  if(target.checked) {
    // Add project ID if not already present
    if(!currentIds.includes(projectId)) {
      userForm.form.notification_settings[key] = [...currentIds, projectId];
    }
  } else {
    // Remove project ID
    userForm.form.notification_settings[key] = currentIds.filter((id) => id !== projectId);
  }
};

// Remove project from selection
const removeProject = (key: string, projectId: number) => {
  const currentIds = getSelectedProjectIds(key);

  userForm.form.notification_settings[key] = currentIds.filter((id) => id !== projectId);
};
</script>

<style scoped></style>
